// Drilling Data Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';
import { authenticatedFetch } from './authenticatedFetch';
import { apiConfig } from '../auth/authConfig';
import { redactAzureBlobUrl } from '../utils/corePhotoDebug';

class DrillingService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    // Configured identically to mx-deposit-sync (proven working setup)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoint for drilling data (Python functions)
    this.drillingApiUrl = `${this.functionAppUrl}/api/drilling_data`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken('qgisApi');
      const userInfo = authService.getUserInfo();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown'
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get ALL drilling data from drilling_master_2025
   * @param {Object} options - Query options
   * @param {number} options.limit - Max records to return
   * @param {number} options.offset - Pagination offset
   * @param {string} options.prospect - Filter by prospect
   * @param {string} options.status - Filter by status
   */
  async getAllDrilling(options = {}) {
    try {
      const {
        limit = 100,
        offset = 0,
        prospect = 'all',
        status = 'all'
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.drillingApiUrl}?endpoint=all-drilling&limit=${limit}&offset=${offset}`;
      
      if (prospect && prospect !== 'all') {
        url += `&prospect=${encodeURIComponent(prospect)}`;
      }
      
      if (status && status !== 'all') {
        url += `&status=${encodeURIComponent(status)}`;
      }

      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch drilling data: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        data: result.data || [],
        pagination: result.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching all drilling data:', error);
      throw error;
    }
  }

  /**
   * Get drilling metrics from real data
   */
  async getMetrics() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(`${this.drillingApiUrl}?endpoint=metrics`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch metrics: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || {};
    } catch (error) {
      console.error('Error fetching drilling metrics:', error);
      throw error;
    }
  }

  /**
   * Get all prospects (like sites, but from your actual data)
   */
  async getProspects() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(`${this.drillingApiUrl}?endpoint=prospects`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch prospects: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching prospects:', error);
      throw error;
    }
  }

  /**
   * Get status summary
   */
  async getStatusSummary() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(`${this.drillingApiUrl}?endpoint=status-summary`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch status summary: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching status summary:', error);
      throw error;
    }
  }

  /**
   * Get all dashboard data for display
   * @param {string} prospect - Optional prospect filter
   * @param {string} status - Optional status filter
   */
  async getDashboardData(prospect = 'all', status = 'all') {
    try {
      // Fetch data in parallel
      const [allDrilling, metrics, prospects] = await Promise.all([
        this.getAllDrilling({ limit: 50, prospect, status }),
        this.getMetrics(),
        this.getProspects()
      ]);

      return {
        drilling: allDrilling.data,
        pagination: allDrilling.pagination,
        metrics,
        prospects
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get coring activity data for production analytics
   * @param {Object} options - Query options
   * @param {string} options.hole_name - Filter by hole name
   * @param {string} options.drill - Filter by drill rig
   * @param {string} options.shift - Filter by shift
   * @param {string} options.start_date - Filter start date (YYYY-MM-DD)
   * @param {string} options.end_date - Filter end date (YYYY-MM-DD)
   * @param {boolean|string} options.billable - Filter by billable status (accepts true/false or 'Y'/'N')
   * @param {string} options.activity - Filter by activity name
   * @param {string} options.activity_type - Filter by activity type
   * @param {number} options.limit - Limit results (per page when fetchAll is true)
   * @param {boolean} options.fetchAll - When true, paginate through all records until complete
   * 
   * ⚠️ API UPDATED (Feb 2026): billable now returns 'Y'/'N', activity_hours (not hours),
   * activity_type/activity (not activity_code/activity_description)
   */
  async getCoringActivity(options = {}) {
    const { fetchAll = false, ...fetchOptions } = options;
    const BATCH_SIZE = 5000;

    const fetchPage = async (offset = 0) => {
      const params = new URLSearchParams();
      if (fetchOptions.hole_name) params.append('hole_name', fetchOptions.hole_name);
      if (fetchOptions.drill) params.append('drill', fetchOptions.drill);
      if (fetchOptions.activity) params.append('activity', fetchOptions.activity);
      if (fetchOptions.activity_type) params.append('activity_type', fetchOptions.activity_type);
      if (fetchOptions.shift) params.append('shift', fetchOptions.shift);
      if (fetchOptions.start_date) params.append('start_date', fetchOptions.start_date);
      if (fetchOptions.end_date) params.append('end_date', fetchOptions.end_date);
      if (fetchOptions.billable !== undefined) params.append('billable', fetchOptions.billable);
      params.append('limit', fetchAll ? BATCH_SIZE : (fetchOptions.limit || BATCH_SIZE));
      params.append('offset', offset);

      const queryString = params.toString();
      const url = `${this.functionAppUrl}/api/coring_activity?${queryString}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s per batch when paginating

      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: await this.getAuthenticatedHeaders(),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Coring activity API error:', response.status, errorText);
        throw new Error(`Failed to fetch coring activity: ${response.statusText}`);
      }

      return response.json();
    };

    try {
      if (fetchAll) {
        console.log('📡 Fetching coring activity data (paginated, fetchAll):', fetchOptions);
        const allData = [];
        let offset = 0;
        let hasMore = true;
        let pageNum = 1;

        while (hasMore) {
          const result = await fetchPage(offset);
          const batch = result.data || [];
          allData.push(...batch);
          hasMore = result.pagination?.has_more ?? false;
          offset = result.pagination?.next_offset ?? offset + batch.length;

          console.log(`📄 Batch ${pageNum}: ${batch.length} records (total: ${allData.length})`);
          pageNum++;

          if (batch.length === 0) break;
        }

        console.log('✅ Coring activity data loaded (all pages):', allData.length, 'records');
        return allData;
      }

      console.log('📡 Fetching coring activity data:', fetchOptions);
      const result = await fetchPage(0);
      console.log('✅ Coring activity data loaded:', result.data?.length || 0, 'records');
      return result.data || [];
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('❌ Coring activity request timed out');
      } else {
        console.error('❌ Error fetching coring activity:', error);
      }
      throw error;
    }
  }

  /**
   * Get drill trace (3D line geometry) for a specific hole
   * Returns array of {x, y, z} points from the trace LineString
   * @param {string} holeId - The hole_id to get trace for
   */
  async getDrillTrace(holeId) {
    try {
      console.log('📡 Fetching drill trace from API:', holeId);
      const headers = await this.getAuthenticatedHeaders();
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await authenticatedFetch(
        `${this.functionAppUrl}/api/drill_traces?hole_id=${encodeURIComponent(holeId)}`,
        { 
          method: 'GET', 
          headers,
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API error response:', response.status, errorText);
        throw new Error(`Failed to fetch drill trace: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📊 API response:', result);
      
      // Return the points array for the trace, or empty if not found
      if (result.data && result.data.length > 0 && result.data[0].points) {
        console.log('✅ Found trace with', result.data[0].points.length, 'points');
        return result.data[0].points;
      }
      
      console.warn('⚠️ No trace data in API response');
      return [];
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('❌ Drill trace request timed out after 15 seconds');
      } else {
        console.error('❌ Error fetching drill trace:', error);
      }
      return []; // Return empty array on error
    }
  }

  /**
   * Get downhole data (lithology or magnetic susceptibility) for one or more drill holes
   * @param {string|string[]} holeIds - Single hole_id string or array of hole_ids
   * @param {'lithology'|'mag_sus'|'mineralization'|'alteration'} dataType - Type of downhole data
   * @returns {Promise<Array>} Array of interval objects with hole_id, from_depth, to_depth, and value fields
   */
  async getDownholeData(holeIds, dataType) {
    try {
      const headers = await this.getAuthenticatedHeaders();

      const controller = new AbortController();
      const timeoutMs = Array.isArray(holeIds) ? 30000 : 15000;
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const idParam = Array.isArray(holeIds)
        ? holeIds.map(id => encodeURIComponent(id)).join(',')
        : encodeURIComponent(holeIds);

      const url = `${this.functionAppUrl}/api/downhole_data?type=${encodeURIComponent(dataType)}&hole_id=${idParam}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Downhole data API error:', response.status, errorText);
        throw new Error(`Failed to fetch downhole data: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Downhole data request timed out');
      } else {
        console.error('Error fetching downhole data:', error);
      }
      throw error;
    }
  }

  /**
   * Core photos for one or more holes (metadata + rendition blob paths from minto_db).
   * Requires drilling-dashboard Azure AD group (same as downhole_data).
   *
   * @param {string|string[]} holeIds - Hole id(s); arrays are joined as comma-separated hole_id
   * @param {Object} [options]
   * @param {number} [options.limit=2000]
   * @param {number} [options.offset=0]
   * @param {boolean} [options.includeRenditions=true]
   * @param {boolean} [options.includeSas=true] — mint read SAS URLs (needs AZURE_STORAGE_CONNECTION_STRING on API)
   * @param {number} [options.sasTtlMinutes] — SAS lifetime minutes (5–1440)
   * @param {string} [options.parentFolderContains] - ILIKE substring on parent_folder_path (e.g. WET / DRY)
   * @param {number} [options.depthOverlapFrom] - Requires depthOverlapTo; overlap filter on depth intervals
   * @param {number} [options.depthOverlapTo]
   * @returns {Promise<{ success: boolean, hole_ids: string[], photos: Array, total_matching: number, limit: number, offset: number, include_sas?: boolean, sas_ttl_minutes?: number, sas_expires_at?: string }>}
   */
  async getCorePhotos(holeIds, options = {}) {
    const {
      limit = 2000,
      offset = 0,
      includeRenditions = true,
      includeSas = true,
      sasTtlMinutes,
      parentFolderContains,
      depthOverlapFrom,
      depthOverlapTo,
    } = options;

    try {
      const headers = await this.getAuthenticatedHeaders();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const params = new URLSearchParams();
      params.append('hole_id', Array.isArray(holeIds) ? holeIds.join(',') : holeIds);
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      params.append('include_renditions', includeRenditions ? 'true' : 'false');
      params.append('include_sas', includeSas ? 'true' : 'false');
      if (sasTtlMinutes != null && sasTtlMinutes !== '') {
        params.append('sas_ttl_minutes', String(sasTtlMinutes));
      }
      if (parentFolderContains != null && parentFolderContains !== '') {
        params.append('parent_folder_contains', parentFolderContains);
      }
      if (depthOverlapFrom != null && depthOverlapTo != null) {
        params.append('depth_overlap_from', String(depthOverlapFrom));
        params.append('depth_overlap_to', String(depthOverlapTo));
      }

      const url = `${this.functionAppUrl}/api/core_photos?${params.toString()}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Core photos API error:', response.status, errorText);
        throw new Error(`Failed to fetch core photos: ${response.statusText}`);
      }

      const result = await response.json();
      const photos = result.photos || [];
      if (process.env.NODE_ENV === 'development' && photos.length > 0) {
        const p0 = photos[0];
        console.info('[core-photos:api] first row snapshot (dev only)', {
          count: photos.length,
          include_sas: result.include_sas,
          sas_expires_at: result.sas_expires_at ?? null,
          hole_id: p0.hole_id,
          file_name: p0.file_name,
          blob_container: p0.blob_container,
          blob_path: p0.blob_path,
          has_top_level_sas_url: Boolean(p0.sas_url),
          sas_url_redacted: p0.sas_url ? redactAzureBlobUrl(p0.sas_url) : null,
          rendition_keys:
            p0.renditions && typeof p0.renditions === 'object'
              ? Object.keys(p0.renditions)
              : [],
        });
      }
      return {
        success: result.success !== false,
        hole_ids: result.hole_ids || [],
        photos,
        total_matching: result.total_matching ?? 0,
        limit: result.limit ?? limit,
        offset: result.offset ?? offset,
        include_sas: result.include_sas ?? includeSas,
        sas_ttl_minutes: result.sas_ttl_minutes,
        sas_expires_at: result.sas_expires_at,
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Core photos request timed out');
      } else {
        console.error('Error fetching core photos:', error);
      }
      throw error;
    }
  }

  /**
   * Get nearby drill traces within a spatial radius of a selected hole.
   * All coordinates are returned relative to the selected hole's collar.
   * @param {string} holeId - Center hole ID
   * @param {number} radius - Search radius in meters (0 = selected hole only, max 1000)
   * @param {number|null} azimuth - Optional slice direction in degrees from north (0-360)
   * @param {number|null} sliceWidth - Optional corridor width in meters
   * @returns {Promise<{holeCount: number, data: Array<{hole_id: string, points: Array}>}>}
   */
  async getNearbyTraces(holeId, radius = 0, azimuth = null, sliceWidth = null) {
    try {
      const headers = await this.getAuthenticatedHeaders();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const params = new URLSearchParams();
      params.append('hole_id', holeId);
      params.append('radius', radius);
      if (azimuth !== null) params.append('azimuth', azimuth);
      if (sliceWidth !== null) params.append('slice_width', sliceWidth);

      const url = `${this.functionAppUrl}/api/nearby_traces?${params.toString()}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Nearby traces API error:', response.status, errorText);
        throw new Error(`Failed to fetch nearby traces: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        holeCount: result.hole_count || 0,
        data: result.data || [],
        collarWorld: result.collar_world || null,
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Nearby traces request timed out');
      } else {
        console.error('Error fetching nearby traces:', error);
      }
      throw error;
    }
  }

  /**
   * Planned drilling rows from drilling_progress.planned_drilling (drilling-dashboard group).
   * @param {Record<string, string|number>} [filters] - query params (only rows with lat+lon returned; date overlap uses estimated_complete + days_estimate — see API doc)
   */
  async getPlannedDrilling(filters = {}) {
    const headers = await this.getAuthenticatedHeaders();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v) !== '') {
          params.append(k, String(v));
        }
      });
      const url = `${this.functionAppUrl}/api/planned_drilling?${params.toString()}`;
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const text = await response.text();
      let result;
      try {
        result = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`Planned drilling: invalid JSON (${response.status})`);
      }
      if (!response.ok) {
        throw new Error(result?.message || result?.error || `Failed to fetch planned drilling: ${response.statusText}`);
      }
      return {
        success: result.success !== false,
        data: result.data || [],
        pagination: result.pagination || {},
        meta: result.meta || {},
      };
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === 'AbortError') console.error('Planned drilling request timed out');
      else console.error('Error fetching planned drilling:', e);
      throw e;
    }
  }

  /**
   * GLTF/GLB surface assets near a hole (dxf_gltf_service), for 3D viewer.
   * @param {string} holeId
   * @param {number} [radius=500] search radius in meters (max 2000)
   * @param {object|null} [collarOverride]
   * @param {{ forceClientCollar?: boolean }} [opts] force client E/N/Z for bbox (Drillz-only retry)
   * @returns {Promise<{ assets: Array, collarWorld: object|null }>}
   */
  async getGltfAssets(holeId, radius = 500, collarOverride = null, opts = {}) {
    const headers = await this.getAuthenticatedHeaders();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    const params = new URLSearchParams();
    params.set('hole_id', holeId);
    params.set('radius', String(radius));
    if (opts.forceClientCollar) {
      params.set('collar_source', 'client');
    }
    if (collarOverride?.easting != null) {
      params.set('easting', String(collarOverride.easting));
    }
    if (collarOverride?.northing != null) {
      params.set('northing', String(collarOverride.northing));
    }
    if (collarOverride?.elevation != null) {
      params.set('elevation', String(collarOverride.elevation));
    }
    const url = `${this.functionAppUrl}/api/gltf_assets?${params.toString()}`;
    try {
      const response = await authenticatedFetch(url, { method: 'GET', headers, signal: controller.signal });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to fetch GLTF assets: ${response.status} ${text}`);
      }
      const result = await response.json();
      return {
        assets: result.assets || [],
        collarWorld: result.collar_world || null,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Drill-hole rows + metrics header (combined view + drilling master).
   * @returns {Promise<{ data: Array }>}
   */
  async fetchDashboardDrillingRows() {
    const headers = await this.getAuthenticatedHeaders();
    const response = await authenticatedFetch(`${this.functionAppUrl}/api/drilling_data`, { headers });
    if (!response.ok) {
      let errBody = null;
      try {
        errBody = await response.json();
      } catch {
        errBody = { raw: await response.text().catch(() => '') };
      }
      // #region agent log
      fetch('http://127.0.0.1:7836/ingest/e71b2ef9-f9ac-414e-aecc-0212abf6b534', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a1699d' },
        body: JSON.stringify({
          sessionId: 'a1699d',
          runId: 'pre-fix',
          hypothesisId: 'A-E',
          location: 'drillingService.js:fetchDashboardDrillingRows',
          message: 'drilling_data API error response',
          data: {
            status: response.status,
            statusText: response.statusText,
            error: errBody?.error,
            apiMessage: errBody?.message,
            errorType: errBody?.type,
            url: `${this.functionAppUrl}/api/drilling_data`,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      const detail = errBody?.message || errBody?.error || `HTTP ${response.status}`;
      throw new Error(`Drilling API failed: ${detail}`);
    }
    const payload = await response.json();
    // #region agent log
    fetch('http://127.0.0.1:7836/ingest/e71b2ef9-f9ac-414e-aecc-0212abf6b534', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a1699d' },
      body: JSON.stringify({
        sessionId: 'a1699d',
        runId: 'pre-fix',
        hypothesisId: 'success',
        location: 'drillingService.js:fetchDashboardDrillingRows',
        message: 'drilling_data API success',
        data: { rowCount: Array.isArray(payload?.data) ? payload.data.length : null },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    return payload;
  }

  /**
   * Registry of drilling campaigns / projects (drilling_scmi.project_sources).
   * @returns {Promise<Array>} list of source rows
   */
  async fetchProjectSources() {
    const headers = await this.getAuthenticatedHeaders();
    const response = await authenticatedFetch(`${this.functionAppUrl}/api/project_sources`, { headers });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`project_sources returned ${response.status} ${text}`);
    }
    const result = await response.json();
    return result.data || [];
  }

  /**
   * Drillz campaign progress rows from tenant_data.v_drillz_campaign_progress.
   * @returns {Promise<Array|null>}
   */
  async fetchDrillzCampaignProgress() {
    const headers = await this.getAuthenticatedHeaders();
    const response = await authenticatedFetch(`${this.functionAppUrl}/api/drillz_campaign_progress`, { headers });
    if (!response.ok) {
      console.warn('Drillz campaign progress not available:', response.status);
      return null;
    }
    const result = await response.json();
    if (!result.success || !Array.isArray(result.data)) {
      return null;
    }
    return result.data;
  }

  /**
   * Recent in-progress holes for a Drillz campaign (daily report hole picker).
   * @param {string} campaignId
   * @param {{ limit?: number }} [options]
   * @returns {Promise<{ success: boolean, data: Array, error?: string }>}
   */
  async fetchDrillzInProgressHoles(campaignId, options = {}) {
    if (!campaignId) {
      throw new Error('campaignId is required');
    }

    const limit = options.limit ?? 15;
    const headers = await this.getAuthenticatedHeaders();
    const url = `${this.functionAppUrl}/api/drillz_in_progress_holes?campaign_id=${encodeURIComponent(
      campaignId
    )}&limit=${encodeURIComponent(String(limit))}`;
    const response = await authenticatedFetch(url, { headers });

    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const errBody = await response.json();
        detail = errBody.message || errBody.error || detail;
      } catch {
        detail = await response.text().catch(() => detail);
      }
      throw new Error(`Drillz in-progress holes unavailable: ${detail}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || result.message || 'Drillz in-progress holes request failed');
    }

    return {
      success: true,
      data: Array.isArray(result.data) ? result.data : [],
      campaignId: result.campaign_id ?? campaignId,
    };
  }

  /**
   * Quicklog intervals for one or more collar UUIDs.
   * @param {string|string[]} collarUuids
   * @returns {Promise<{ success: boolean, data: Array, by_collar_uuid: Object }>}
   */
  async fetchDrillzQuicklog(collarUuids) {
    const ids = (Array.isArray(collarUuids) ? collarUuids : [collarUuids])
      .filter((id) => id != null && String(id).trim() !== '')
      .map((id) => String(id));

    if (!ids.length) {
      return { success: true, data: [], by_collar_uuid: {} };
    }

    const headers = await this.getAuthenticatedHeaders();
    const query =
      ids.length === 1
        ? `collar_uuid=${encodeURIComponent(ids[0])}`
        : `collar_uuids=${encodeURIComponent(ids.join(','))}`;
    const url = `${this.functionAppUrl}/api/drillz_quicklog?${query}`;
    const response = await authenticatedFetch(url, { headers });

    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const errBody = await response.json();
        detail = errBody.message || errBody.error || detail;
      } catch {
        detail = await response.text().catch(() => detail);
      }
      throw new Error(`Drillz quicklog unavailable: ${detail}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || result.message || 'Drillz quicklog request failed');
    }

    return {
      success: true,
      data: Array.isArray(result.data) ? result.data : [],
      by_collar_uuid: result.by_collar_uuid && typeof result.by_collar_uuid === 'object'
        ? result.by_collar_uuid
        : {},
    };
  }

  /**
   * Drillz tenant_app list values with displayColor / texture metadata.
   * Resolves lists via table_definitions + field_definitions.list_id (default quicklog fields).
   * @param {object|string[]|undefined} options
   * @param {string} [options.tableCode]
   * @param {string[]} [options.fieldCodes]
   * @param {string[]} [options.listCodes] legacy list_definitions.list_code filter only
   * @returns {Promise<{ success: boolean, lists: Object }>}
   */
  async fetchDrillzListValues(options) {
    let tableCode = 'quicklog';
    let fieldCodes = ['rock_type', 'min_style', 'min_type'];
    let listCodes = null;

    if (Array.isArray(options)) {
      fieldCodes = options;
    } else if (options && typeof options === 'object') {
      if (options.tableCode) tableCode = String(options.tableCode).trim();
      if (Array.isArray(options.fieldCodes) && options.fieldCodes.length) {
        fieldCodes = options.fieldCodes;
      }
      if (Array.isArray(options.listCodes) && options.listCodes.length) {
        listCodes = options.listCodes;
      }
    }

    fieldCodes = fieldCodes
      .filter((code) => code != null && String(code).trim() !== '')
      .map((code) => String(code).trim());

    const headers = await this.getAuthenticatedHeaders();
    let query;
    if (listCodes && !options?.tableCode) {
      query = `list_codes=${encodeURIComponent(listCodes.join(','))}`;
    } else {
      if (!fieldCodes.length) {
        return { success: true, lists: {} };
      }
      query = [
        `table_code=${encodeURIComponent(tableCode)}`,
        `field_codes=${encodeURIComponent(fieldCodes.join(','))}`,
      ].join('&');
    }

    const url = `${this.functionAppUrl}/api/drillz_list_values?${query}`;
    const response = await authenticatedFetch(url, { headers });

    if (!response.ok) {
      console.warn(`Drillz list values unavailable: HTTP ${response.status}`);
      return { success: false, lists: {} };
    }

    const result = await response.json();
    if (!result.success) {
      console.warn('Drillz list values request failed:', result.error || result.message);
      return { success: false, lists: {} };
    }

    return {
      success: true,
      lists: result.lists && typeof result.lists === 'object' ? result.lists : {},
    };
  }

  /**
   * Sample progress: per-project rows (key: source_project_id) + pre-aggregated totals for the All tab.
   * @returns {Promise<{ projects: Array, aggregated: object|null } | null>}
   */
  async fetchSampleProgressDashboard() {
    const headers = await this.getAuthenticatedHeaders();
    const response = await authenticatedFetch(`${this.functionAppUrl}/api/sample_progress`, { headers });
    if (!response.ok) {
      console.warn('Sample progress not available:', response.status);
      return null;
    }
    const result = await response.json();
    const payload = result.data;
    if (payload && Array.isArray(payload.projects)) {
      return {
        projects: payload.projects,
        aggregated: payload.aggregated ?? null,
      };
    }
    // Legacy single-row response
    if (payload && typeof payload === 'object' && payload.total_samples_designated !== undefined) {
      return {
        projects: [payload],
        aggregated: payload,
      };
    }
    return null;
  }

  /**
   * Download GLB for a DXF→GLB asset via Function proxy (avoids CORS on blob storage).
   * @param {number} assetId
   * @returns {Promise<ArrayBuffer>}
   */
  async fetchGltfGlbArrayBuffer(assetId) {
    const baseHeaders = await this.getAuthenticatedHeaders();
    const headers = {
      Authorization: baseHeaders.Authorization,
    };
    if (baseHeaders['x-user-id']) {
      headers['x-user-id'] = baseHeaders['x-user-id'];
    }
    const url = `${this.functionAppUrl}/api/gltf_glb_proxy?asset_id=${encodeURIComponent(String(assetId))}`;
    const response = await authenticatedFetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`GLB proxy failed: ${response.status} ${text}`);
    }
    return response.arrayBuffer();
  }
}

// Export singleton instance
export const drillingService = new DrillingService();
export default drillingService;

